import React, { useEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import { BarChart3, RefreshCw, Calendar } from 'lucide-react';
import { Modal } from '@/components/Modal';
import { Button } from '@/components/Button';
import { GoogleAdsNotice } from './GoogleAdsShell';
import { errMsg } from './youtube/youtubeShared';
import { googleAdsDateRange } from './reportingUtils';
import {
  googleAdsService,
  GoogleAdsDailyRow,
  GoogleAdsReportLevel,
} from '@/services/google-ads.service';

type Metric = 'impressions' | 'clicks' | 'cost' | 'conversions' | 'conversionsValue';

const METRIC_LABELS: Record<Metric, string> = {
  impressions:      'Impressions',
  clicks:           'Clicks',
  cost:             'Cost',
  conversions:      'Conversions',
  conversionsValue: 'Conv. value',
};

interface Props {
  open: boolean;
  onClose: () => void;
  accountUid: string;
  level: GoogleAdsReportLevel;
  entityId: string;
  entityName?: string;
  days: number;
  currency?: string;
  timeZone?: string;
}


const DailyBars: React.FC<{ rows: GoogleAdsDailyRow[]; metric: Metric; currency?: string }> = ({ rows, metric, currency }) => {
  const values = rows.map(r => Number(r[metric]) || 0);
  const max = Math.max(...values, 1);
  const W = 820;
  const H = 180;
  const barW = rows.length > 0 ? W / rows.length : 0;
  const fmt = (v: number) => metric === 'cost' || metric === 'conversionsValue'
    ? v.toFixed(2)
    : v.toLocaleString();

  return (
    <svg width="100%" viewBox={`0 0 ${W} ${H + 22}`} className="block">
      {values.map((v, i) => {
        const h = max > 0 ? (v / max) * (H - 8) : 0;
        return (
          <g key={i}>
            <rect
              x={i * barW + 2}
              y={H - h}
              width={Math.max(barW - 4, 1)}
              height={h}
              fill="currentColor"
              className="text-primary"
              opacity={0.7 + 0.3 * (v / max)}
              rx={2}
            >
              <title>{`${rows[i].date}: ${fmt(v)}${(metric === 'cost' || metric === 'conversionsValue') && currency ? ' ' + currency : ''}`}</title>
            </rect>
            {v > 0 && barW > 36 && (
              <text
                x={i * barW + barW / 2}
                y={H - h - 3}
                fontSize="9"
                textAnchor="middle"
                className="fill-text-muted tabular-nums"
              >
                {fmt(v)}
              </text>
            )}
            <text
              x={i * barW + barW / 2}
              y={H + 14}
              fontSize="9"
              textAnchor="middle"
              className="fill-text-muted tabular-nums"
            >
              {rows[i].date.slice(5)}
            </text>
          </g>
        );
      })}
      <line x1={0} y1={H} x2={W} y2={H} className="stroke-border" strokeWidth={1} />
    </svg>
  );
};

export const GoogleAdsDailyBreakdownModal: React.FC<Props> = ({
  open, onClose, accountUid, level, entityId, entityName, days, currency, timeZone,
}) => {
  const [rows, setRows] = useState<GoogleAdsDailyRow[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState('');
  const version = useRef(0);
  const [metric, setMetric] = useState<Metric>('cost');

  const load = async () => {
    const request = ++version.current;
    setLoading(true); setError('');
    try {
      const { from, to } = googleAdsDateRange(days, timeZone);
      const r = await googleAdsService.dailyReport(accountUid, { level, entityId, from, to });
      if (request === version.current) setRows(r);
    } catch (e) {
      if (request === version.current) setError(errMsg(e));
    } finally { if (request === version.current) setLoading(false); }
  };

  useEffect(() => {
    if (!open) return;
    setRows(null);
    load();
    return () => { version.current += 1; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, accountUid, entityId, level, days, timeZone]);

  const sync = async () => {
    setSyncing(true);
    try {
      const r = await googleAdsService.syncReport(accountUid, { daysBack: days, level });
      toast.success(`Synced ${r.upserted} rows over ${r.days} days`);
      await load();
    } catch {
      // Axios interceptor toasts the backend message; avoid duplicates.
    } finally { setSyncing(false); }
  };

  const totals = useMemo(() => {
    const t = { impressions: 0, clicks: 0, cost: 0, conversions: 0, conversionsValue: 0 };
    (rows ?? []).forEach((r) => {
      t.impressions += r.impressions;
      t.clicks += r.clicks;
      t.cost += r.cost;
      t.conversions += r.conversions;
      t.conversionsValue += r.conversionsValue;
    });
    return t;
  }, [rows]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Daily breakdown · ${level} ${entityName ?? entityId}`}
      widthClass="max-w-4xl"
    >
      <div className="space-y-4">
        {error && <GoogleAdsNotice error title="Daily report unavailable">{error}<Button size="sm" variant="ghost" onClick={load}>Retry</Button></GoogleAdsNotice>}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="text-xs text-text-muted">
            Showing the last <strong className="text-text-primary">{days}</strong> days from your local cache.
          </div>
          <div className="flex items-center gap-2">
            <div className="inline-flex rounded-lg border border-border bg-surface-alt p-0.5 gap-0.5 text-2xs">
              {(Object.keys(METRIC_LABELS) as Metric[]).map((m) => {
                const active = metric === m;
                return (
                  <button
                    key={m}
                    type="button"
                    onClick={() => setMetric(m)}
                    className={clsx(
                      'rounded-md px-2 py-1 font-medium transition-all',
                      active ? 'bg-surface text-text-primary shadow-soft' : 'text-text-muted hover:text-text-primary',
                    )}
                  >
                    {METRIC_LABELS[m]}
                  </button>
                );
              })}
            </div>
            <Button variant="secondary" size="sm" loading={syncing} onClick={sync}>
              <RefreshCw size={12} /> Re-sync
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-2 sm:grid-cols-5">
          {[
            { label: 'Impressions',     value: totals.impressions.toLocaleString() },
            { label: 'Clicks',          value: totals.clicks.toLocaleString() },
            { label: `Cost${currency ? ' (' + currency + ')' : ''}`, value: totals.cost.toFixed(2) },
            { label: 'Conv',            value: totals.conversions.toLocaleString() },
            { label: 'Conv. value',     value: totals.conversionsValue.toFixed(2) },
          ].map((k) => (
            <div key={k.label} className="rounded-xl border border-border bg-surface-alt/40 p-2 text-center">
              <div className="text-2xs uppercase tracking-wide text-text-muted">{k.label}</div>
              <div className="mt-0.5 font-display text-base font-bold text-text-primary tabular-nums">{k.value}</div>
            </div>
          ))}
        </div>

        <div className="rounded-2xl border border-border bg-surface p-4">
          {loading && <div className="skeleton h-44 rounded-lg" />}
          {!loading && rows && rows.length === 0 && (
            <div className="flex flex-col items-center gap-2 py-8 text-center">
              <BarChart3 size={28} className="text-text-muted" />
              <p className="text-sm font-semibold text-text-primary">No daily rows in your local cache</p>
              <p className="text-xs text-text-muted max-w-sm">
                Either this entity hasn't run yet, or you haven't synced. Click <strong>Re-sync</strong> above
                to pull the latest {days} days from Google Ads into your DB.
              </p>
            </div>
          )}
          {!loading && rows && rows.length > 0 && (
            <>
              <div className="mb-2 flex items-center gap-2">
                <BarChart3 size={14} className="text-primary" />
                <span className="text-xs font-semibold text-text-primary">
                  Daily {METRIC_LABELS[metric]} · {rows[0].date} → {rows[rows.length - 1].date}
                </span>
              </div>
              <DailyBars rows={rows} metric={metric} currency={currency} />
            </>
          )}
        </div>

        {rows && rows.length > 0 && (
          <div className="rounded-2xl border border-border bg-surface overflow-hidden">
            <div className="flex items-center gap-2 px-4 py-2 border-b border-border bg-surface-alt/40">
              <Calendar size={12} className="text-text-muted" />
              <span className="text-2xs uppercase tracking-wide font-semibold text-text-muted">Per-day rows from your DB</span>
            </div>
            <div className="max-h-72 overflow-y-auto">
              <table className="w-full text-xs">
                <thead className="border-b border-border bg-surface text-2xs uppercase tracking-wide text-text-muted sticky top-0">
                  <tr>
                    <th className="px-4 py-2 text-left">Day</th>
                    <th className="px-4 py-2 text-left">Date</th>
                    <th className="px-4 py-2 text-right">Impr</th>
                    <th className="px-4 py-2 text-right">Clicks</th>
                    <th className="px-4 py-2 text-right">CTR</th>
                    <th className="px-4 py-2 text-right">Cost</th>
                    <th className="px-4 py-2 text-right">Avg CPC</th>
                    <th className="px-4 py-2 text-right">Conv</th>
                    <th className="px-4 py-2 text-right">Conv. value</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {rows.map((r, i) => (
                    <tr key={r.date} className="hover:bg-surface-alt">
                      <td className="px-4 py-2 font-mono text-text-muted tabular-nums">Day {i + 1}</td>
                      <td className="px-4 py-2 font-mono text-text-primary tabular-nums">{r.date}</td>
                      <td className="px-4 py-2 text-right tabular-nums">{r.impressions.toLocaleString()}</td>
                      <td className="px-4 py-2 text-right tabular-nums">{r.clicks.toLocaleString()}</td>
                      <td className="px-4 py-2 text-right tabular-nums">{r.ctr ? r.ctr.toFixed(2) + '%' : '—'}</td>
                      <td className="px-4 py-2 text-right tabular-nums">{r.cost.toFixed(2)}</td>
                      <td className="px-4 py-2 text-right tabular-nums">{r.averageCpc ? r.averageCpc.toFixed(2) : '—'}</td>
                      <td className="px-4 py-2 text-right tabular-nums">{r.conversions.toLocaleString()}</td>
                      <td className="px-4 py-2 text-right tabular-nums">{r.conversionsValue.toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};
